<script setup>
const props = defineProps({
  show: Boolean,
  title: { type: String, default: "Éxito" },
  message: { type: String, default: "" },
  icon: { type: String, default: "check_circle" },
});

const emit = defineEmits(["close"]);

function close() {
  emit("close");
}

// Auto-close after 3 seconds
let timeout;
if (props.show) {
  timeout = setTimeout(() => {
    close();
  }, 3000);
}

// Clear timeout on unmount
onBeforeUnmount(() => {
  if (timeout) clearTimeout(timeout);
});
</script>

<script>
import { onBeforeUnmount } from "vue";
</script>

<template>
  <Transition name="modal">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <div class="modal-content">
          <div class="success-icon-wrapper">
            <span class="material-symbols-rounded success-icon">{{
              icon
            }}</span>
          </div>
          <h2 class="modal-title">{{ title }}</h2>
          <p v-if="message" class="modal-message">{{ message }}</p>
          <button class="close-btn" @click="close">Cerrar</button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* ===== MODAL OVERLAY ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* ===== MODAL CONTAINER ===== */
.modal-container {
  position: relative;
  background: var(--panel-bg);
  border: 1.5px solid var(--panel-border);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  color: var(--text);
  backdrop-filter: blur(20px);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ===== MODAL CONTENT ===== */
.modal-content {
  padding: 2.5rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
}

.success-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.3);
}

@keyframes scaleIn {
  from {
    transform: scale(0);
    opacity: 0;
  }

  to {
    transform: scale(1);
    opacity: 1;
  }
}

.success-icon {
  font-size: 3rem;
  color: #fff;
  animation: checkmark 0.6s ease-in-out 0.2s backwards;
}

@keyframes checkmark {
  0% {
    transform: scale(0) rotate(-45deg);
    opacity: 0;
  }

  50% {
    transform: scale(1.2) rotate(0deg);
  }

  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.modal-title {
  font-size: 1.75rem;
  font-weight: 900;
  margin: 0;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.modal-message {
  color: var(--muted);
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
  font-weight: 500;
}

.close-btn {
  margin-top: 0.5rem;
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
}

.close-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.4);
}

.close-btn:active {
  transform: translateY(0);
}

/* ===== TRANSITIONS ===== */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}

/* ===== RESPONSIVE DESIGN ===== */
@media (max-width: 640px) {
  .modal-container {
    max-width: 100%;
    margin: 0 0.5rem;
    border-radius: 20px;
  }

  .modal-content {
    padding: 2rem 1.5rem;
  }

  .success-icon-wrapper {
    width: 70px;
    height: 70px;
  }

  .success-icon {
    font-size: 2.5rem;
  }

  .modal-title {
    font-size: 1.5rem;
  }

  .modal-message {
    font-size: 0.95rem;
  }
}

@media (max-width: 380px) {
  .modal-container {
    margin: 0 0.25rem;
  }

  .modal-content {
    padding: 1.75rem 1.25rem;
  }
}
</style>
